import { Component, OnInit } from "@angular/core";
import { CommonModule } from "@angular/common";
import {
  FormBuilder,
  FormGroup,
  Validators,
  ReactiveFormsModule,
  AbstractControl,
  ValidationErrors,
} from "@angular/forms";
import { RouterModule } from "@angular/router";
import { CustomNotificationService } from "../../services/custom-notification.service";
import { WebApiService } from "../../services/web-api.service";
import { LucideAngularModule } from "lucide-angular";

@Component({
  selector: "app-register",
  standalone: true,
  imports: [
    CommonModule,
    ReactiveFormsModule,
    RouterModule,
    LucideAngularModule,
  ],
  templateUrl: "./register.component.html",
  styleUrl: "./register.component.css",
})
export class RegisterComponent implements OnInit {
  registerForm!: FormGroup;
  submitted = false;
  showPassword = false;
  loading = false;
  checkingUsername = false;
  usernameAvailable: boolean | null = null;

  constructor(
    private fb: FormBuilder,
    private api: WebApiService,
    private notificationService: CustomNotificationService,
  ) {}

  ngOnInit(): void {
    this.registerForm = this.fb.group({
      firstName: ["", Validators.required],
      lastName: ["", Validators.required],
      email: ["", [Validators.required, Validators.email]],
      phoneNumber: ["", Validators.required],
      username: ["", [Validators.required, this.usernameValidator()]],
      password: ["", [Validators.required, this.passwordValidator()]],
    });
  }

  private passwordValidator() {
    return (control: AbstractControl): ValidationErrors | null => {
      const value = control.value;
      if (!value) return null;

      const hasUpperCase = /[A-Z]/.test(value);
      const hasNumber = /[0-9]/.test(value);
      const hasSymbol = /[!@#$%^&*()_+\-=\[\]{}|;':",./<>?]/.test(value);
      const isLongEnough = value.length >= 8;

      const valid = hasUpperCase && hasNumber && hasSymbol && isLongEnough;
      return valid ? null : { passwordStrength: true };
    };
  }

  private usernameValidator() {
    return (control: AbstractControl): ValidationErrors | null => {
      const value = control.value;
      if (!value) return null;

      const hasSpaces = /\s/.test(value);
      return hasSpaces ? { noSpaces: true } : null;
    };
  }

  get hasMinLength(): boolean {
    return (this.f["password"].value?.length || 0) >= 8;
  }

  get hasUpperCase(): boolean {
    return /[A-Z]/.test(this.f["password"].value || "");
  }

  get hasNumber(): boolean {
    return /[0-9]/.test(this.f["password"].value || "");
  }

  get hasSymbol(): boolean {
    return /[!@#$%^&*()_+\-=\[\]{}|;':",./<>?]/.test(
      this.f["password"].value || "",
    );
  }

  onUsernameBlur(): void {
    const username = this.f["username"].value;
    if (!username || username.length < 3 || /\s/.test(username)) {
      this.usernameAvailable = null;
      return;
    }

    this.checkingUsername = true;
    this.api.get(`User/check-username/${username}`).subscribe({
      next: (exists: any) => {
        this.usernameAvailable = !exists;
        this.checkingUsername = false;
      },
      error: () => {
        this.usernameAvailable = null;
        this.checkingUsername = false;
      },
    });
  }

  get f() {
    return this.registerForm.controls;
  }

  onRegister(): void {
    this.submitted = true;
    if (this.registerForm.invalid) {
      return;
    }
    this.loading = true;
    const payload = {
      username: this.f["username"].value,
      name: this.f["firstName"].value,
      last_name: this.f["lastName"].value,
      email: this.f["email"].value,
      phone_number: this.f["phoneNumber"].value,
      password: this.f["password"].value,
    };
    this.api.post("User", payload).subscribe({
      next: () => {
        this.loading = false;
        this.registerForm.reset();
        this.submitted = false;
        this.notificationService.success(
          "Registration Complete",
          "Welcome to ReBall! Please check your email and confirm your email address to start booking courts.",
        );
      },
      error: (err) => {
        this.loading = false;
        const detail = err?.error?.detail || err?.error?.message || "";
        const detailLower = detail.toLowerCase();

        if (
          detailLower.includes("username") &&
          (detailLower.includes("taken") ||
            detailLower.includes("exists") ||
            detailLower.includes("already"))
        ) {
          this.usernameAvailable = false;
          this.notificationService.error(
            "Username Taken",
            "This username is already in use. Please choose a different one.",
          );
        } else if (
          detailLower.includes("email") &&
          (detailLower.includes("taken") ||
            detailLower.includes("exists") ||
            detailLower.includes("already"))
        ) {
          this.notificationService.error(
            "Email Taken",
            "This email is already registered. Please use a different email or sign in.",
          );
        } else {
          this.notificationService.error(
            "Error",
            detail || "Registration failed. Please try again.",
          );
        }
      },
    });
  }

  togglePasswordVisibility(): void {
    this.showPassword = !this.showPassword;
  }
}
